import classnames from 'classnames'
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Image, ImageProps } from '@src/quince-components/src'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { SnapScrollHandle, SnapScroll } from '@src/quince-components/src/molecules/snap-scroll'
import { MAX_DESKTOP_WIDTH, MAX_MOBILE_WIDTH } from '@src/quince-site-common/src/constants/app'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TAG_LOCATION, TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import {
  getProductPropertiesForTrackingNew,
  PRODUCT_GALLERY_MEDIA_TYPE,
} from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { ProductCardAddToWishlist } from '../product-card-actions/add-to-wishlist'
import { ProductCardBuyItNow } from '../product-card-actions/buy-it-now'
import { ProductCardDeleteFromWishlist } from '../product-card-actions/delete-wishlist'
import { ProductCardLinkProps, ProductCardLink } from '../product-card-actions/product-card-link'
import { useProductCardHover } from '../product-card-context/product-card-hover.context'
import { CardVariant, IProductCard, ProductCardTag } from '../product-card.types'

import { useProductImageProps } from './product-image-section.hooks'
import styles from './product-image-section.module.scss'
import { ProductTags } from './product-tags'
import { useShowBuyNowBasedOnDeviceType } from './utils'

export interface ProductImageSectionProps extends ProductCardLinkProps {
  productCard: IProductCard
  selectedCardVariant?: CardVariant
  gender?: GenderName | undefined
  imageRef?: MutableRefObject<HTMLImageElement | null> | undefined
  maxWidth?: number
  eagerLoadImage?: boolean
  wishlistIconClassName?: string
  wishlistIconClass?: string
  addToCartClassName?: string
  showDeleteBtn?: boolean
  deleteItemClassName?: string
  quality?: number
  className?: string
  applicableTags?: ProductCardTag[]
  hideWishlistIcon?: boolean
  hideBuyNowIcon?: boolean
  hideTags?: boolean
  quickBuyEnabled?: boolean
  wishlistEnabled?: boolean
  readyToShipEnabled?: boolean
  siteSearchQuery?: string
  productPosition?: number
  productSearchObjectId?: string
  showSecondImageOnProductTiles?: boolean
  disableImageHoverSwap?: boolean
  productClickTrackConfig?: TrackConfig
  wishlistTrackConfig?: Record<string, unknown>
  showMScroll?: boolean
  quickBuyOverride?: () => void
  forceShowBuyNow?: boolean
  tabData?: Record<string, string | boolean | { i: number; j: number }>
}

// @i18n-ignore
const PRODUCT_CARD_TRACKER = 'product_card'

export const ProductImageSection = (props: ProductImageSectionProps) => {
  const [keySwiper, setKeySwiper] = useState(0) // State to force remount
  // In-grid carousel (mScroll) only: non-first slides withhold their <Image> until the user first
  // engages the scroller. `loading="lazy"` alone does NOT hold the budget — every slide of an
  // above-the-fold card sits at that card's vertical position, so the browser fetches the lazy slides
  // during initial load anyway, tripling image payload and starving the LCP image on throttled mobile.
  // One-way latch: flipped by the first touch (pointer-down) or any settled scroll.
  const [carouselActivated, setCarouselActivated] = useState(false)
  const { isHovered, setHovered } = useProductCardHover()
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const DEFAULT_MAX_PRODUCT_CARD_WIDTH = isMobile ? MAX_MOBILE_WIDTH : MAX_DESKTOP_WIDTH / 2

  const swiperRef = useRef<SnapScrollHandle>(null)
  const effectiveIsHovered = props.disableImageHoverSwap ? false : isHovered
  const productImageProps = useProductImageProps(
    props.selectedCardVariant,
    props.gender,
    isMobile,
    effectiveIsHovered,
    props.showSecondImageOnProductTiles,
    props.showMScroll,
  )

  // Fires the same `productGalleryViewed` event the DS carousel + PDP gallery fire, each time the
  // customer swipes to a new image in the in-grid carousel (mobile + experiment `show` bucket only).
  // NOTE: This replaces the old `productSwiperScrollEventTrigger` / `PRODUCT_CARD_SCROLLED` event.
  // The constant, type entry, and InternalAnalytics handler are now dead code; they will be removed
  // once we confirm no live Mixpanel dashboard reads `productCardScrolled`.
  const handleOnScrollEvent = useCallback(() => {
    if (!Array.isArray(productImageProps)) return
    setCarouselActivated(true) // fallback: keyboard / programmatic scroll never fires pointer-down
    const index = swiperRef.current?.getActiveIndex()
    if (index == null) return
    const image = productImageProps[index]
    if (!image) return
    const tags = props.applicableTags?.map(tag => tag.value) ?? []
    trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_VIEWED, {
      product_id: String(props.productCard.product.relatedIds.spid),
      context: {
        product_position: props.productPosition,
        productProperties: {
          ...getProductPropertiesForTrackingNew({
            product: props.productCard.product,
            productVariant: props.selectedCardVariant,
            tags,
          }),
          product_gallery_photo_id: image.id,
          product_gallery_photo_position: index + 1,
          product_gallery_media_type: PRODUCT_GALLERY_MEDIA_TYPE.PHOTO,
        },
      },
    })
  }, [productImageProps, props.applicableTags, props.productCard, props.selectedCardVariant, props.productPosition])

  const price = props.selectedCardVariant?.price?.salePrice
  const selectedColor = props.selectedCardVariant?.displayConfig.value
  const trackingContext = {
    product_price: price,
    clicked_prodid: `${props.productCard.product.productId}_${selectedColor}`,
  }
  const wishlistTrackingConfig = {
    productId: props.productCard.product.relatedIds.spid,
    color: selectedColor,
    trackerSection: PRODUCT_CARD_TRACKER,
    tags: props.applicableTags,
    product_search_query: props.siteSearchQuery,
    product_position: props.productPosition,
    context: {
      ...props.wishlistTrackConfig,
      ...trackingContext,
    },
  }

  const firstProductImageId = Array.isArray(productImageProps) ? productImageProps[0]?.id : productImageProps.id
  // The useEffect should only trigger when the active index changes,
  // and not for other interactions like hovering on desktop or clicking on mobile.
  // Simply including the product image array as a dependency may cause unnecessary re-renders.
  useEffect(() => {
    if (Array.isArray(productImageProps) && swiperRef.current?.getActiveIndex() !== 0) {
      setKeySwiper(k => k + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstProductImageId])

  const showBuyNowBasedOnDeviceType = useShowBuyNowBasedOnDeviceType(isMobile)
  const showBuyNowBase = !props.hideBuyNowIcon && props.quickBuyEnabled && showBuyNowBasedOnDeviceType
  const showBuyNow = props.forceShowBuyNow ?? showBuyNowBase

  const imageAltText = useMemo(() => {
    return selectedColor ? `${selectedColor} ${props.title}` : props.title
  }, [selectedColor, props.title])

  return (
    <div
      className={classnames(styles.imageSection, props.className)}
      onMouseEnter={() => {
        setHovered(true)
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
    >
      <div
        className={styles.imageContainer}
        // First touch on the carousel latches activation so the deferred slides mount and stream in.
        // Capture phase fires before the scroll begins, so images are requested as the swipe starts.
        onPointerDownCapture={
          Array.isArray(productImageProps)
            ? () => {
                setCarouselActivated(true)
              }
            : undefined
        }
      >
        {Array.isArray(productImageProps) ? (
          <SnapScroll
            key={keySwiper}
            ref={swiperRef}
            spaceBetween={10}
            scrollEventDebounceTime={250}
            handleOnScrollEvent={handleOnScrollEvent}
            // `keySwiper` is part of the id so a color/variant change resets the carousel to the first
            // image. On variant change we bump `keySwiper` to remount the scroller, but SnapScroll
            // saves its scroll position on unmount and restores it on mount keyed by this id — and the
            // spid is identical across colors, so a stable id would restore the pre-change index and
            // leave the carousel stuck on the last-viewed image. Bumping the id alongside the remount
            // gives the fresh mount a cache miss, so it starts at index 0.
            id={`product-image-section-${props.productCard.product.relatedIds.spid}-${keySwiper}`}
          >
            {productImageProps.map((productImg: ImageProps, imageIndex: number) => {
              return (
                <ProductCardLink
                  key={productImg.id}
                  slug={props.slug}
                  additionalAttributes={props.additionalAttributes}
                  selectorKey={props.selectorKey}
                  tracker={props.tracker}
                  productCardLinkClass={props.productCardLinkClass}
                  selectedCardVariant={props.selectedCardVariant}
                  siteSearchQuery={props.siteSearchQuery}
                  productPosition={props.productPosition}
                  productSearchObjectId={props.productSearchObjectId}
                  title={props.title}
                  spid={props.spid}
                  reviewSummary={props.reviewSummary}
                  productClickTrackConfig={props.productClickTrackConfig}
                  colorVariants={props.colorVariants}
                  eddDetails={props.eddDetails}
                  product={props.product}
                  onClickCb={props.onClickCb}
                >
                  {imageIndex === 0 || carouselActivated ? (
                    <Image
                      {...productImg}
                      alt={imageAltText}
                      quality={props.quality}
                      ref={props.imageRef}
                      className={styles.productImage}
                      maxWidth={props.maxWidth ?? DEFAULT_MAX_PRODUCT_CARD_WIDTH}
                      // First slide follows the grid's first-row eager hint; the rest defer to protect LCP.
                      // @i18n-ignore
                      loading={imageIndex === 0 && props.eagerLoadImage ? 'eager' : 'lazy'}
                    />
                  ) : (
                    // Deferred slide: empty box (NO <Image>, so no network request) until the carousel is
                    // engaged. Passing url='' to <Image> is not enough — it still emits a src (the page
                    // URL with image query params) and fetches it. The box fills the slide so there is no
                    // layout shift and the snap geometry is unchanged. `role="img"` + `aria-label` keep the
                    // wrapping PDP link named (same accessible name as the real image) so keyboard / screen
                    // reader users never tab to an unnamed link before activation.
                    <div role="img" aria-label={imageAltText} className={styles.productImage} />
                  )}
                </ProductCardLink>
              )
            })}
          </SnapScroll>
        ) : (
          <Image
            {...productImageProps}
            alt={imageAltText}
            quality={props.quality}
            ref={props.imageRef}
            className={styles.productImage}
            maxWidth={props.maxWidth ?? DEFAULT_MAX_PRODUCT_CARD_WIDTH}
            // @i18n-ignore
            loading={props.eagerLoadImage ? 'eager' : 'lazy'}
          />
        )}
      </div>

      <div className={styles.imageOverlay}>
        {showBuyNow ? (
          <ProductCardBuyItNow
            productCard={props.productCard}
            selectedCardVariant={props.selectedCardVariant}
            addToCartClassName={classnames(props.addToCartClassName, styles.buyItNowBtn)}
            siteSearchQuery={props.siteSearchQuery}
            productPosition={props.productPosition}
            productSearchObjectId={props.productSearchObjectId}
            productClickTrackConfig={props.productClickTrackConfig}
            quickBuyOverride={props.quickBuyOverride}
            tabData={props.tabData}
          />
        ) : null}
        {!props.hideWishlistIcon && props.wishlistEnabled && (
          <ProductCardAddToWishlist
            wishlistIconClassName={props.wishlistIconClassName}
            spidWishlist={props.productCard.product.relatedIds.spid}
            selectedCardVariant={props.selectedCardVariant}
            wishlistTrackingConfig={wishlistTrackingConfig}
            wishlistIconClass={props.wishlistIconClass}
            {...props}
          />
        )}
        {!isMobile && props.showDeleteBtn && props.wishlistEnabled ? (
          <ProductCardDeleteFromWishlist
            deleteItemClassName={classnames(props.deleteItemClassName, styles.deleteBtn)}
            spid={props.productCard.product.relatedIds.spid}
            selectedCardVariant={props.selectedCardVariant}
            wishlistTrackingConfig={wishlistTrackingConfig}
          />
        ) : null}
        {props.hideTags ? null : (
          <ProductTags
            applicableTags={props.applicableTags}
            readyToShip={props.readyToShipEnabled}
            className={styles.productTag}
            position={TAG_POSITION.BOTTOM}
            tagLocation={TAG_LOCATION.PRODUCT_CARD}
          />
        )}
      </div>
    </div>
  )
}
